import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useUser } from '../../contexts/UserContext';
import {
  Box,
  Flex,
  Heading,
  Text,
  SimpleGrid,
  Card,
  CardHeader,
  CardBody,
  VStack,
  HStack,
  Badge,
  Icon,
  Progress,
  Spinner,
  useColorModeValue,
  Select,
  Input,
  InputGroup,
  InputLeftElement,
  Divider,
  Tooltip,
  Grid,
  GridItem,
  Avatar,
  Button,
  Collapse,
} from '@chakra-ui/react';
import {
  FaSearch,
  FaStar,
  FaChartLine,
  FaComments,
  FaGlobe,
  FaCheckCircle,
  FaExclamationTriangle,
  FaInfoCircle,
  FaTrophy,
  FaArrowUp,
  FaArrowDown,
  FaChevronDown,
  FaChevronUp,
  FaCalculator,
  FaBalanceScale,
  FaRocket,
  FaChartBar,
  FaMedal,
  FaAward,
} from 'react-icons/fa';
import {
  HiOutlineTrendingUp,
} from 'react-icons/hi';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Cell,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
  Legend,
  PieChart,
  Pie,
} from 'recharts';
import AnimatedBox from '../AnimatedBox';
import { SectionLabel } from './DashboardWrapper';
import { 
  getLocalyticsRankings, 
  calculateRankingInsights, 
  getRankingTier, 
  getRankingColor, 
  formatRanking,
  getOrdinal
} from '../../services/localyticsRankingService';
import { getBusinessImages } from '../../services/imageService';
import { logger } from '../../utils/logger';
import BusinessReviewModal from '../BusinessReviewModal';
import { createFavoriteStarPieLabelRenderer } from '../FavoriteStarIndicator';

// Score tier configuration (0-100 scale, higher is better)
const SCORE_TIERS = [
  { min: 85, max: 100, label: 'Excellent', color: 'blue', description: 'Top-tier market leader', icon: FaTrophy },
  { min: 70, max: 84, label: 'Good', color: 'green', description: 'Solid market performance', icon: FaRocket },
  { min: 55, max: 69, label: 'Fair', color: 'teal', description: 'Room to grow', icon: FaArrowUp },
  { min: 40, max: 54, label: 'Below Average', color: 'yellow', description: 'Significant room for improvement', icon: FaBalanceScale },
  { min: 25, max: 39, label: 'Poor', color: 'orange', description: 'Needs focused effort', icon: FaArrowDown },
  { min: 0, max: 24, label: 'Needs Improvement', color: 'red', description: 'Start with the fundamentals', icon: FaExclamationTriangle },
];

const getRankingTierConfig = (score, totalBusinesses) => {
  if (!score && score !== 0) return SCORE_TIERS[SCORE_TIERS.length - 1];
  
  // Find matching tier based on score (0-100 scale)
  for (const tier of SCORE_TIERS) {
    if (score >= tier.min && score <= tier.max) {
      return tier;
    }
  }
  return SCORE_TIERS[SCORE_TIERS.length - 1];
};

// Business Ranking Card Component
const BusinessRankingCard = ({ business, position, totalBusinesses, isExpanded, onToggle, onBusinessClick }) => {
  const { brandName } = useUser();
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const textColor = useColorModeValue('gray.700', 'gray.200');
  const subtitleColor = useColorModeValue('gray.500', 'gray.400');
  const hoverBg = useColorModeValue('gray.50', 'gray.700');
  const gridStroke = useColorModeValue('#E2E8F0', '#4A5568');
  const tickFill = useColorModeValue('#4A5568', '#A0AEC0');

  const tier = getRankingTier(business.localytics_score, totalBusinesses, position);
  const rankingColor = getRankingColor(business.localytics_score, totalBusinesses);

  // Radar data for component visualization
  // Score per category: max(0, 21 - rank), shown as percentage of max 20 points
  const radarData = [
    { subject: 'Growth', A: business.rankings?.growth ? (Math.max(0, 21 - business.rankings.growth) / 20) * 100 : 0, fullMark: 100 },
    { subject: 'Reviews', A: business.rankings?.total_reviews ? (Math.max(0, 21 - business.rankings.total_reviews) / 20) * 100 : 0, fullMark: 100 },
    { subject: 'Sentiment', A: business.rankings?.sentiment ? (Math.max(0, 21 - business.rankings.sentiment) / 20) * 100 : 0, fullMark: 100 },
    { subject: 'Traffic', A: business.rankings?.traffic && business.rankings.traffic !== 'N/A' ? (Math.max(0, 21 - business.rankings.traffic) / 20) * 100 : 0, fullMark: 100 },
    { subject: 'SEO', A: business.rankings?.seo && business.rankings.seo !== 'N/A' ? (Math.max(0, 21 - business.rankings.seo) / 20) * 100 : 0, fullMark: 100 },
  ];

  return (
    <AnimatedBox animation="fadeIn" delay={position * 0.05} duration={0.5}>
      <Card
        bg={cardBg}
        borderWidth="1px"
        borderColor={borderColor}
        borderRadius="xl"
        overflow="hidden"
        transition="all 0.3s"
        _hover={{ shadow: 'lg', borderColor: 'blue.300', transform: 'translateY(-2px)' }}
        mb={4}
      >
        <CardBody p={0}>
          {/* Main Info Row - Always visible */}
          <Flex
            p={5}
            cursor="pointer"
            onClick={onToggle}
            _hover={{ bg: hoverBg }}
            transition="background 0.2s"
            align="center"
            justify="space-between"
          >
            <HStack spacing={4} flex={1}>
              {/* Position Badge */}
              <Flex
                w="40px"
                h="40px"
                borderRadius="full"
                bg={position <= 3 ? (position === 1 ? 'yellow.400' : position === 2 ? 'gray.300' : 'orange.300') : 'blue.100'}
                justify="center"
                align="center"
                fontWeight="bold"
                color={position <= 3 ? 'white' : 'blue.600'}
                fontSize="lg"
              >
                {position}
              </Flex>

              {/* Business Name and Tier */}
              <VStack align="start" spacing={0} flex={1}>
                <Text 
                  fontWeight="bold" 
                  fontSize="lg" 
                  color={textColor} 
                  noOfLines={1}
                  cursor="pointer"
                  _hover={{ color: 'blue.500', textDecoration: 'underline' }}
                  onClick={(e) => {
                    e.stopPropagation();
                    onBusinessClick && onBusinessClick(business);
                  }}
                >
                  {business.business_name}
                </Text>
                <HStack spacing={2}>
                  <Badge colorScheme={tier.colorScheme} fontSize="xs" px={2} py={0.5} borderRadius="full">
                    {tier.emoji} {tier.label}
                  </Badge>
                </HStack>
              </VStack>
            </HStack>

            {/* Score and Ranking Display */}
            <HStack spacing={4}>
              {/* Localytics Score - plain number */}
              <VStack spacing={0} align="center">
                <Text fontWeight="bold" fontSize="3xl" color="black">
                  {business.localytics_score || 0}
                </Text>
                <Text fontSize="xs" color={subtitleColor} fontWeight="600">
                  {brandName} Score
                </Text>
              </VStack>

              <Icon
                as={isExpanded ? FaChevronUp : FaChevronDown}
                color={subtitleColor}
                boxSize={5}
              />
            </HStack>
          </Flex>

          {/* Expanded Details */}
          <Collapse in={isExpanded} animateOpacity>
            <Box borderTopWidth="1px" borderColor={borderColor}>
              <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={6} p={5}>
                {/* Component Rankings */}
                <VStack align="stretch" spacing={4}>
                  <HStack>
                    <Icon as={FaCalculator} color="blue.500" />
                    <Text fontWeight="semibold" color={textColor}>Category Rankings</Text>
                  </HStack>
                  
                  {[
                    { key: 'growth', label: 'Growth', icon: HiOutlineTrendingUp, description: 'Review growth momentum' },
                    { key: 'total_reviews', label: 'Total Reviews', icon: FaComments, description: 'Overall review volume' },
                    { key: 'sentiment', label: 'Sentiment', icon: FaStar, description: 'Customer satisfaction' },
                    { key: 'traffic', label: 'Web Traffic', icon: FaGlobe, description: 'Digital engagement' },
                    { key: 'seo', label: 'SEO Score', icon: FaSearch, description: 'Search engine performance' },
                  ].map((comp) => {
                    const rank = business.rankings?.[comp.key];
                    const isValid = rank && rank !== 'N/A';
                    const percentile = isValid ? (Math.max(0, 21 - rank) / 20) * 100 : 0;
                    
                    return (
                      <Box key={comp.key}>
                        <Flex justify="space-between" mb={1}>
                          <HStack>
                            <Icon as={comp.icon} color="gray.500" boxSize={4} />
                            <Text fontSize="sm" fontWeight="medium" color={textColor}>
                              {comp.label}
                            </Text>
                          </HStack>
                          <HStack>
                            <Badge colorScheme={isValid && rank <= 3 ? 'green' : 'gray'} fontSize="xs">
                              {isValid ? getOrdinal(rank) : 'N/A'}
                            </Badge>
                            <Text fontSize="sm" fontWeight="bold" color={isValid && rank <= 3 ? 'green.500' : textColor}>
                              of {totalBusinesses}
                            </Text>
                          </HStack>
                        </Flex>
                        <Progress
                          value={percentile}
                          size="sm"
                          borderRadius="full"
                          colorScheme={percentile >= 80 ? 'green' : percentile >= 50 ? 'blue' : 'gray'}
                        />
                        <Text fontSize="xs" color={subtitleColor} mt={1}>
                          {comp.description}
                        </Text>
                      </Box>
                    );
                  })}

                </VStack>

                {/* Radar Chart */}
                <VStack align="stretch" spacing={4}>
                  <HStack>
                    <Icon as={FaChartBar} color="purple.500" />
                    <Text fontWeight="semibold" color={textColor}>Performance Profile</Text>
                  </HStack>
                  <Box h="220px">
                    <ResponsiveContainer width="100%" height="100%">
                      <RadarChart data={radarData}>
                        <PolarGrid stroke={gridStroke} />
                        <PolarAngleAxis
                          dataKey="subject"
                          tick={{ fontSize: 14, fill: tickFill, fontWeight: 600 }}
                        />
                        <PolarRadiusAxis
                          angle={30}
                          domain={[0, 100]}
                          tick={{ fontSize: 12, fill: tickFill, fontWeight: 600 }}
                        />
                        <Radar
                          name="Percentile"
                          dataKey="A"
                          stroke={rankingColor}
                          fill={rankingColor}
                          fillOpacity={0.5}
                        />
                      </RadarChart>
                    </ResponsiveContainer>
                  </Box>
                  <Text fontSize="xs" color={subtitleColor} textAlign="center">
                    Higher percentile = better ranking in category
                  </Text>
                </VStack>
              </SimpleGrid>
            </Box>
          </Collapse>
        </CardBody>
      </Card>
    </AnimatedBox>
  );
};

// Main Dashboard Component
const LocalyticsScoreDashboard = ({ demoMode, demoData }) => {
  const [rankingData, setRankingData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('ranking');
  const [expandedCards, setExpandedCards] = useState({});
  const [businessImages, setBusinessImages] = useState({});
  
  // State for BusinessReviewModal
  const [selectedBusiness, setSelectedBusiness] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [reviewsData, setReviewsData] = useState([]);
  const [yearData, setYearData] = useState([]);
  
  // Get favorite URIs from user context
  const { favoriteUris, brandName } = useUser();

  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const textColor = useColorModeValue('gray.700', 'gray.200');
  const subtitleColor = useColorModeValue('gray.500', 'gray.400');
  const bgGradient = useColorModeValue(
    'linear(to-br, blue.50, purple.50)',
    'linear(to-br, gray.800, gray.900)'
  );
  const gridStrokeColor = useColorModeValue('#E2E8F0', '#4A5568');
  const explainerBg = useColorModeValue('gray.50', 'gray.700');
  const codeBg = useColorModeValue('gray.100', 'gray.600');

  useEffect(() => {
    if (demoMode && demoData) {
      setRankingData(demoData.rankingData || {});
      setReviewsData(demoData.rankingData?.businesses || []);
      setYearData(demoData.rankingData?.yearData || []);
      setIsLoading(false);
      return;
    }
    const fetchData = async () => {
      setIsLoading(true);
      setError(null);
      try {
        logger.log('Fetching Localytics Rankings...');
        
        const rankings = await getLocalyticsRankings();
        setRankingData(rankings);
        setReviewsData(rankings.businesses || rankings.sortedByGrowth || []);
        setYearData(rankings.yearData || []);
        
        logger.log('Rankings loaded:', rankings);
      } catch (err) {
        logger.error('Error fetching data:', err);
        setError(err.message);
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, [demoMode, demoData]);

  // Fetch business images
  useEffect(() => {
    if (demoMode) return;
    let mounted = true;
    
    const fetchImages = async () => {
      try {
        const images = await getBusinessImages().catch(err => {
          logger.log('Images not available:', err.message);
          return [];
        });
        
        if (mounted) {
          if (images) {
            const nameToImage = {};
            images.forEach(img => {
              if (img.business_name && img.high_res_image_url_fetched) {
                nameToImage[img.business_name] = img.high_res_image_url_fetched;
              }
            });
            setBusinessImages(nameToImage);
          }
        }
      } catch (error) {
        logger.error('Error fetching images:', error);
      }
    };

    fetchImages();
    
    return () => {
      mounted = false;
    };
  }, [demoMode]);

  const insights = useMemo(() => {
    if (!rankingData?.rankingResults) return null;
    return calculateRankingInsights(rankingData.rankingResults);
  }, [rankingData]);

  // Get top 3 businesses for podium (highest scores)
  const top3Businesses = useMemo(() => {
    if (!rankingData?.rankingResults) return [];
    return rankingData.rankingResults
      .filter(b => b.localytics_score !== null && b.localytics_score !== undefined)
      .slice(0, 3);
  }, [rankingData]);

  // Create a map from business names to maps_uri for favorites check
  const businessNameToUriMap = useMemo(() => {
    const map = {};
    if (rankingData?.rankingResults) {
      rankingData.rankingResults.forEach(business => {
        if (business.business_name && business.maps_uri) {
          map[business.business_name] = business.maps_uri;
        }
      });
    }
    return map;
  }, [rankingData]);
  
  // Custom label renderer for bar charts that shows spinning stars for favorited businesses
  const FavoriteStarBarLabel = useCallback((props) => {
    const { x, y, width, payload } = props;
    const datumName = payload?.business_name ?? payload?.name ?? props?.name;
    
    // Check if this business is favorited
    const mapsUri = payload?.maps_uri || payload?.mapsUri || businessNameToUriMap[datumName];
    const isFavorited = mapsUri && favoriteUris?.includes(mapsUri);
    
    if (!isFavorited) return null;
    
    const starSize = 18;
    const starX = x + (width / 2);
    // Keep the star within the visible SVG area (top bars can otherwise clip it)
    const starY = Math.max(y - starSize - 4, 2);
    return (
      <g transform={`translate(${starX - starSize/2}, ${starY})`}>
        <g>
          <animateTransform
            attributeName="transform"
            attributeType="XML"
            type="rotate"
            from={`0 ${starSize/2} ${starSize/2}`}
            to={`360 ${starSize/2} ${starSize/2}`}
            dur="3s"
            repeatCount="indefinite"
          />
          <path
            d={`M${starSize/2 + 1},${2} L${starSize*0.63 + 1},${starSize*0.38 + 2} L${starSize + 1},${starSize*0.38 + 2} L${starSize*0.69 + 1},${starSize*0.62 + 2} L${starSize*0.81 + 1},${starSize + 2} L${starSize/2 + 1},${starSize*0.75 + 2} L${starSize*0.19 + 1},${starSize + 2} L${starSize*0.31 + 1},${starSize*0.62 + 2} L${1},${starSize*0.38 + 2} L${starSize*0.37 + 1},${starSize*0.38 + 2} Z`}
            fill="#8B7355"
            fillOpacity="0.3"
          />
          <path
            d={`M${starSize/2},0 L${starSize*0.63},${starSize*0.38} L${starSize},${starSize*0.38} L${starSize*0.69},${starSize*0.62} L${starSize*0.81},${starSize} L${starSize/2},${starSize*0.75} L${starSize*0.19},${starSize} L${starSize*0.31},${starSize*0.62} L0,${starSize*0.38} L${starSize*0.37},${starSize*0.38} Z`}
            fill="#D4AF37"
            stroke="#B8860B"
            strokeWidth="1"
          />
          <path
            d={`M${starSize/2},0 L${starSize*0.63},${starSize*0.38} L${starSize},${starSize*0.38} L${starSize*0.69},${starSize*0.62} L${starSize*0.81},${starSize} L${starSize/2},${starSize*0.75} L${starSize*0.19},${starSize} L${starSize*0.31},${starSize*0.62} L0,${starSize*0.38} L${starSize*0.37},${starSize*0.38} Z`}
            fill="#FFD700"
            fillOpacity="0.7"
          />
          <ellipse
            cx={starSize/2}
            cy={starSize*0.3}
            rx={starSize*0.18}
            ry={starSize*0.14}
            fill="#FFFACD"
            fillOpacity="0.8"
          />
        </g>
      </g>
    );
  }, [businessNameToUriMap, favoriteUris]);

  const filteredAndSortedData = useMemo(() => {
    if (!rankingData?.rankingResults) return [];

    let filtered = rankingData.rankingResults.filter(b => 
      b.localytics_score !== null && b.localytics_score !== undefined
    );

    // Apply search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(b => 
        b.business_name.toLowerCase().includes(query)
      );
    }

    // Apply sorting
    switch (sortBy) {
      case 'ranking':
        // Already sorted by score (higher is better)
        break;
      case 'name':
        filtered = [...filtered].sort((a, b) => a.business_name.localeCompare(b.business_name));
        break;
      case 'growth':
        filtered = [...filtered].sort((a, b) => 
          (a.rankings?.growth || 999) - (b.rankings?.growth || 999)
        );
        break;
      case 'reviews':
        filtered = [...filtered].sort((a, b) => 
          (a.rankings?.total_reviews || 999) - (b.rankings?.total_reviews || 999)
        );
        break;
      default:
        break;
    }

    return filtered;
  }, [rankingData, searchQuery, sortBy]);

  const toggleCard = (businessName) => {
    setExpandedCards(prev => ({
      ...prev,
      [businessName]: !prev[businessName]
    }));
  };

  const tierDistribution = useMemo(() => {
    if (!rankingData?.rankingResults) return [];
    
    const validResults = rankingData.rankingResults.filter(b => 
      b.localytics_score !== null && b.localytics_score !== undefined
    );
    const totalBusinesses = validResults.length;
    
    const distribution = SCORE_TIERS.map(tier => ({
      name: tier.label,
      value: validResults.filter(
        b => b.localytics_score >= tier.min && b.localytics_score <= tier.max
      ).length,
      color: tier.color,
    })).filter(d => d.value > 0);

    return distribution;
  }, [rankingData]);

  // Sorting arrays for BusinessReviewModal
  const sortedByTotal = useMemo(() => {
    if (!reviewsData.length) return [];
    return [...reviewsData].sort((a, b) => (b.rating_count || 0) - (a.rating_count || 0));
  }, [reviewsData]);

  const sortedBySentiment = useMemo(() => {
    if (!reviewsData.length) return [];
    return [...reviewsData].sort((a, b) => {
      const aScore = typeof a.sentimentScore === 'number' ? a.sentimentScore : 0;
      const bScore = typeof b.sentimentScore === 'number' ? b.sentimentScore : 0;
      return bScore - aScore;
    });
  }, [reviewsData]);

  const sortedByGrowth = useMemo(() => {
    if (!reviewsData.length) return [];
    return [...reviewsData].sort((a, b) => {
      const aGrowth = typeof a.threeMonthGrowth === 'number' ? a.threeMonthGrowth : 0;
      const bGrowth = typeof b.threeMonthGrowth === 'number' ? b.threeMonthGrowth : 0;
      return bGrowth - aGrowth;
    });
  }, [reviewsData]);

  const sortedByTraffic = useMemo(() => {
    if (!reviewsData.length) return [];
    return [...reviewsData]
      .filter((business) => (business.trafficValue || 0) > 0)
      .sort((a, b) => (b.trafficValue || 0) - (a.trafficValue || 0));
  }, [reviewsData]);

  // Handler for clicking on a business to open BusinessReviewModal
  const handleBusinessClick = useCallback((business) => {
    if (!business) return;

    const businessMapsUri = business.maps_uri || business.mapsUri;
    const businessName = business.business_name || business.name;
    const businessData =
      reviewsData.find(b => businessMapsUri && b.maps_uri === businessMapsUri) ||
      reviewsData.find(b => businessName && b.name === businessName);
    if (businessData) {
      setSelectedBusiness(businessData);
      setIsModalOpen(true);
    }
  }, [reviewsData]);

  if (isLoading) {
    return (
      <Flex justify="center" align="center" minH="400px" direction="column">
        <Spinner size="xl" color="blue.500" thickness="4px" />
        <Text mt={4} color={subtitleColor}>Calculating {brandName} Rankings...</Text>
        <Text fontSize="sm" color={subtitleColor} mt={2}>
          Analyzing business performance across multiple categories
        </Text>
      </Flex>
    );
  }

  if (error) {
    return (
      <Flex justify="center" align="center" minH="400px" direction="column">
        <Icon as={FaExclamationTriangle} color="red.500" boxSize={12} mb={4} />
        <Text color="red.500" fontSize="lg" fontWeight="bold">Error Loading Data</Text>
        <Text color={subtitleColor} mt={2}>{error}</Text>
      </Flex>
    );
  }

  const totalBusinesses = rankingData?.totalBusinesses || 0;

  const TIER_COLORS = {
    purple: '#9F7AEA',
    blue: '#4299E1',
    green: '#48BB78',
    teal: '#38B2AC',
    yellow: '#ECC94B',
    orange: '#ED8936',
    red: '#F56565',
  };

  return (
    <Box>
      {/* Top 3 Podium - Only show on desktop and tablet */}
      {top3Businesses.length >= 3 && (
        <Box
          display={{ base: 'none', md: 'block' }}
          bg="transparent"
          mb={6}
        >
          <Flex justify="center" align="flex-end" gap={6} w="full">
            {/* Silver - Second Place */}
            <Box
              bg={cardBg}
              borderRadius="2xl"
              textAlign="center"
              position="relative"
              transition="all 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
              _hover={{ 
                transform: 'translateY(-8px)', 
                shadow: '2xl',
              }}
              overflow="hidden"
              flex="1"
              maxW="400px"
              boxShadow="xl"
              borderWidth="1px"
              borderColor={borderColor}
              cursor="pointer"
              onClick={() => top3Businesses[1] && handleBusinessClick(top3Businesses[1])}
            >
              <Box h="4px" bgGradient="linear(to-r, gray.300, gray.500, gray.300)" />
              <VStack p={6} spacing={4}>
                <Flex
                  position="absolute"
                  top={4}
                  left={4}
                  minW="36px"
                  h="36px"
                  px={2}
                  borderRadius="full"
                  bgGradient="linear(to-br, gray.300, gray.500)"
                  justify="center"
                  align="center"
                  shadow="md"
                >
                  <Text color="white" fontWeight="bold" fontSize="md" lineHeight="1">2</Text>
                </Flex>
                
                <Icon as={FaMedal} color="gray.400" boxSize={6} />
                
                <Box p="3px" borderRadius="full" bgGradient="linear(to-br, gray.300, gray.500)">
                  <Avatar 
                    name={top3Businesses[1]?.business_name} 
                    src={businessImages[top3Businesses[1]?.business_name] || ''} 
                    bg="gray.100" 
                    boxSize="70px"
                    borderWidth="3px"
                    borderColor={cardBg}
                  />
                </Box>
                
                <VStack spacing={1}>
                  <Tooltip label={top3Businesses[1]?.business_name} placement="top" hasArrow openDelay={300}>
                    <Heading size="sm" noOfLines={1} color={textColor}>{top3Businesses[1]?.business_name}</Heading>
                  </Tooltip>
                  <Text 
                    fontWeight="extrabold" 
                    fontSize="3xl" 
                    bgGradient="linear(to-r, blue.500, purple.500)"
                    bgClip="text"
                  >
                    {top3Businesses[1]?.localytics_score || 0}
                  </Text>
                  <Text fontSize="xs" color={subtitleColor} fontWeight="medium" textTransform="uppercase" letterSpacing="wider">
                    Score: {formatRanking(top3Businesses[1]?.localytics_score)}
                  </Text>
                </VStack>
              </VStack>
            </Box>

            {/* Gold - First Place */}
            <Box
              bg={cardBg}
              borderRadius="2xl"
              textAlign="center"
              position="relative"
              transition="all 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
              _hover={{ 
                transform: 'translateY(-12px) scale(1.02)', 
                shadow: '0 25px 50px -12px rgba(251, 191, 36, 0.4)',
              }}
              overflow="hidden"
              flex="1.15"
              maxW="450px"
              boxShadow="0 20px 40px -12px rgba(251, 191, 36, 0.3)"
              zIndex={2}
              mb={4}
              borderWidth="1px"
              borderColor="yellow.200"
              cursor="pointer"
              onClick={() => top3Businesses[0] && handleBusinessClick(top3Businesses[0])}
            >
              <Box
                h="6px"
                bgGradient="linear(to-r, yellow.300, yellow.400, orange.400, yellow.400, yellow.300)"
                backgroundSize="200% 100%"
                animation="shimmer 2s linear infinite"
                sx={{
                  '@keyframes shimmer': {
                    '0%': { backgroundPosition: '200% 0' },
                    '100%': { backgroundPosition: '-200% 0' },
                  },
                }}
              />
              <VStack p={8} spacing={4}>
                <Flex
                  position="absolute"
                  top={4}
                  left={4}
                  minW="40px"
                  h="40px"
                  px={2}
                  borderRadius="full"
                  bgGradient="linear(to-br, yellow.300, orange.400)"
                  justify="center"
                  align="center"
                  shadow="lg"
                >
                  <Text color="white" fontWeight="bold" fontSize="lg" lineHeight="1">1</Text>
                </Flex>
                
                <Icon as={FaTrophy} color="yellow.400" boxSize={10} filter="drop-shadow(0 2px 4px rgba(251, 191, 36, 0.5))" />
                
                <Box p="4px" borderRadius="full" bgGradient="linear(to-br, yellow.300, orange.400, yellow.500)" shadow="lg">
                  <Avatar 
                    name={top3Businesses[0]?.business_name} 
                    src={businessImages[top3Businesses[0]?.business_name] || ''} 
                    bg="yellow.50" 
                    boxSize="90px"
                    borderWidth="4px"
                    borderColor={cardBg}
                  />
                </Box>
                
                <VStack spacing={1}>
                  <Tooltip label={top3Businesses[0]?.business_name} placement="top" hasArrow openDelay={300}>
                    <Heading size="md" noOfLines={1} color={textColor}>{top3Businesses[0]?.business_name}</Heading>
                  </Tooltip>
                  <Text 
                    fontWeight="extrabold" 
                    fontSize="4xl" 
                    bgGradient="linear(to-r, yellow.400, orange.400)"
                    bgClip="text"
                  >
                    {top3Businesses[0]?.localytics_score || 0}
                  </Text>
                  <Text fontSize="xs" color={subtitleColor} fontWeight="medium" textTransform="uppercase" letterSpacing="wider">
                    Score: {formatRanking(top3Businesses[0]?.localytics_score)}
                  </Text>
                </VStack>
              </VStack>
            </Box>

            {/* Bronze - Third Place */}
            <Box
              bg={cardBg}
              borderRadius="2xl"
              textAlign="center"
              position="relative"
              transition="all 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
              _hover={{ 
                transform: 'translateY(-8px)', 
                shadow: '2xl',
              }}
              overflow="hidden"
              flex="1"
              maxW="400px"
              boxShadow="xl"
              borderWidth="1px"
              borderColor={borderColor}
              cursor="pointer"
              onClick={() => top3Businesses[2] && handleBusinessClick(top3Businesses[2])}
            >
              <Box h="4px" bgGradient="linear(to-r, orange.200, orange.400, orange.200)" />
              <VStack p={6} spacing={4}>
                <Flex
                  position="absolute"
                  top={4}
                  left={4}
                  minW="36px"
                  h="36px"
                  px={2}
                  borderRadius="full"
                  bgGradient="linear(to-br, orange.300, orange.500)"
                  justify="center"
                  align="center"
                  shadow="md"
                >
                  <Text color="white" fontWeight="bold" fontSize="md" lineHeight="1">3</Text>
                </Flex>
                
                <Icon as={FaAward} color="orange.400" boxSize={6} />
                
                <Box p="3px" borderRadius="full" bgGradient="linear(to-br, orange.300, orange.500)">
                  <Avatar 
                    name={top3Businesses[2]?.business_name} 
                    src={businessImages[top3Businesses[2]?.business_name] || ''} 
                    bg="orange.50" 
                    boxSize="70px"
                    borderWidth="3px"
                    borderColor={cardBg}
                  />
                </Box>
                
                <VStack spacing={1}>
                  <Tooltip label={top3Businesses[2]?.business_name} placement="top" hasArrow openDelay={300}>
                    <Heading size="sm" noOfLines={1} color={textColor}>{top3Businesses[2]?.business_name}</Heading>
                  </Tooltip>
                  <Text 
                    fontWeight="extrabold" 
                    fontSize="3xl" 
                    bgGradient="linear(to-r, orange.400, red.400)"
                    bgClip="text"
                  >
                    {top3Businesses[2]?.localytics_score || 0}
                  </Text>
                  <Text fontSize="xs" color={subtitleColor} fontWeight="medium" textTransform="uppercase" letterSpacing="wider">
                    Score: {formatRanking(top3Businesses[2]?.localytics_score)}
                  </Text>
                </VStack>
              </VStack>
            </Box>
          </Flex>
        </Box>
      )}

      {/* Section Label */}
      {!demoMode && <SectionLabel description="Business performance scores based on growth, reviews, traffic, sentiment, and SEO">
        {brandName} Score Breakdown
      </SectionLabel>}

      {/* Score Breakdown Visual */}
      <AnimatedBox animation="fadeIn" delay={0.2} duration={0.6}>
        <Card bg={cardBg} borderWidth="1px" borderColor={borderColor} borderRadius="xl" mb={6} overflow="hidden">
          <Box h="3px" bgGradient="linear(to-r, green.400, blue.400, purple.400, orange.400, teal.400)" />
          <CardBody px={{ base: 4, md: 8 }} py={6}>
            {/* Header */}
            <HStack spacing={3} mb={5} justify="center">
              <Icon as={FaCalculator} color="blue.500" boxSize={5} />
              <Heading size="md" color={textColor} textAlign="center">
                How the Score is Made
              </Heading>
            </HStack>

            {/* Formula Visual */}
            <Flex
              direction={{ base: 'column', md: 'row' }}
              align="center"
              justify="center"
              gap={{ base: 2, md: 3 }}
              mb={5}
            >
              {[
                { icon: HiOutlineTrendingUp, label: 'Growth', color: 'green', description: 'Review growth momentum' },
                { icon: FaComments, label: 'Reviews', color: 'blue', description: 'Total review volume' },
                { icon: FaStar, label: 'Sentiment', color: 'purple', description: 'Customer satisfaction' },
                { icon: FaGlobe, label: 'Traffic', color: 'orange', description: 'Website visitors' },
                { icon: FaSearch, label: 'SEO', color: 'teal', description: 'Search performance' },
              ].map((category, idx, arr) => (
                <React.Fragment key={category.label}>
                  <VStack
                    spacing={2}
                    p={4}
                    bg={`${category.color}.50`}
                    borderRadius="xl"
                    borderWidth="2px"
                    borderColor={`${category.color}.200`}
                    minW={{ base: 'full', md: '130px' }}
                    maxW={{ base: 'full', md: '160px' }}
                    flex="1"
                    transition="all 0.2s"
                    _hover={{ 
                      transform: 'translateY(-2px)', 
                      shadow: 'md',
                      borderColor: `${category.color}.400`,
                    }}
                  >
                    <Flex
                      w="44px"
                      h="44px"
                      borderRadius="full"
                      bg={`${category.color}.100`}
                      justify="center"
                      align="center"
                    >
                      <Icon as={category.icon} color={`${category.color}.500`} boxSize={5} />
                    </Flex>
                    <Text fontSize="sm" fontWeight="bold" color={textColor}>
                      {category.label}
                    </Text>
                    <Text
                      fontSize="xl"
                      fontWeight="extrabold"
                      color={`${category.color}.500`}
                    >
                      20
                    </Text>
                    <Text fontSize="2xs" color={subtitleColor} textAlign="center" lineHeight="1.3">
                      {category.description}
                    </Text>
                  </VStack>
                  {idx < arr.length - 1 && (
                    <Text
                      fontSize="2xl"
                      fontWeight="bold"
                      color={subtitleColor}
                      display={{ base: 'none', md: 'block' }}
                    >
                      +
                    </Text>
                  )}
                </React.Fragment>
              ))}

              {/* Equals sign and total */}
              <Text
                fontSize="2xl"
                fontWeight="bold"
                color={subtitleColor}
                display={{ base: 'none', md: 'block' }}
              >
                =
              </Text>
              <VStack
                spacing={1}
                p={4}
                bgGradient="linear(to-br, blue.500, purple.600)"
                borderRadius="xl"
                minW={{ base: 'full', md: '110px' }}
                maxW={{ base: 'full', md: '130px' }}
                shadow="lg"
              >
                <Text fontSize="3xl" fontWeight="extrabold" color="white">
                  100
                </Text>
                <Text fontSize="xs" fontWeight="bold" color="whiteAlpha.900" textTransform="uppercase" letterSpacing="wider">
                  Max Score
                </Text>
              </VStack>
            </Flex>

            {/* Scoring explanation */}
            <Box
              bg={explainerBg}
              borderRadius="lg"
              p={4}
              textAlign="center"
            >
              <Text fontSize="sm" color={subtitleColor}>
                Each category awards <strong>up to 20 points</strong> based on your market ranking.{' '}
                <Text as="span" fontFamily="mono" fontSize="xs" bg={codeBg} px={2} py={0.5} borderRadius="md">
                  Points = max(0, 21 − rank)
                </Text>
              </Text>
              <Text fontSize="xs" color={subtitleColor} mt={2}>
                1st place = 20 pts &nbsp;·&nbsp; 5th = 16 pts &nbsp;·&nbsp; 10th = 11 pts &nbsp;·&nbsp; 20th = 1 pt &nbsp;·&nbsp; 21st+ = 0 pts
              </Text>
            </Box>
          </CardBody>
        </Card>
      </AnimatedBox>

      {/* Distribution Charts */}
      <AnimatedBox animation="fadeIn" delay={0.3} duration={0.6}>
        <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={6} mb={6}>
          {/* Score Distribution Bar Chart */}
          <Card bg={cardBg} borderWidth="1px" borderColor={borderColor} borderRadius="xl">
            <CardHeader pb={2}>
              <Heading size="md" color={textColor}>Score Distribution</Heading>
              <Text fontSize="sm" color={subtitleColor}>Higher score = better performance (0-100 scale)</Text>
            </CardHeader>
            <CardBody>
              <Box h="250px">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={filteredAndSortedData.slice(0, 20)} margin={{ top: 10, right: 30, left: 0, bottom: 40 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke={gridStrokeColor} />
                    <XAxis
                      dataKey="business_name"
                      tick={(tickProps) => {
                        const { x, y, payload } = tickProps;
                        const displayName = payload.value.length > 12 ? payload.value.substring(0, 12) + '...' : payload.value;
                        const business = filteredAndSortedData.find(b => b.business_name === payload.value);
                        return (
                          <g transform={`translate(${x},${y})`}>
                            <text
                              x={0}
                              y={0}
                              dy={16}
                              textAnchor="end"
                              fill={subtitleColor}
                              fontSize={13}
                              fontWeight={600}
                              transform="rotate(-45)"
                              style={{ cursor: 'pointer' }}
                              onClick={() => business && handleBusinessClick(business)}
                            >
                              {displayName}
                            </text>
                          </g>
                        );
                      }}
                      height={60}
                    />
                    <YAxis
                      tick={{ fontSize: 10, fill: subtitleColor }}
                      domain={[0, 100]}
                      label={{ value: 'Score (0-100, higher is better)', angle: -90, position: 'insideLeft', fontSize: 10 }}
                    />
                    <RechartsTooltip
                      content={({ active, payload }) => {
                        if (active && payload && payload.length) {
                          const data = payload[0].payload;
                          return (
                            <Box bg={cardBg} p={3} borderRadius="md" boxShadow="lg" borderWidth="1px" borderColor={borderColor}>
                              <Text fontWeight="bold">{data.business_name}</Text>
                              <Text color="blue.500">Score: {formatRanking(data.localytics_score)}</Text>
                              <Text fontSize="xs" color={subtitleColor} mt={1}>Click to view details</Text>
                            </Box>
                          );
                        }
                        return null;
                      }}
                    />
                    <Bar
                      dataKey="localytics_score"
                      radius={[4, 4, 0, 0]}
                      label={FavoriteStarBarLabel}
                      cursor="pointer"
                      onClick={(data) => data && handleBusinessClick(data)}
                    >
                      {filteredAndSortedData.slice(0, 20).map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={getRankingColor(entry.localytics_score, totalBusinesses)} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            </CardBody>
          </Card>

          {/* Tier Distribution Pie Chart */}
          <Card bg={cardBg} borderWidth="1px" borderColor={borderColor} borderRadius="xl">
            <CardHeader pb={2}>
              <Heading size="md" color={textColor}>Performance Tiers</Heading>
            </CardHeader>
            <CardBody>
              <Box h="250px">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={tierDistribution}
                      dataKey="value"
                      nameKey="name"
                      cx="50%"
                      cy="50%"
                      outerRadius={80}
                      label={({ name, value }) => `${name}: ${value}`}
                      labelLine
                    >
                      {tierDistribution.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={TIER_COLORS[entry.color] || '#4299E1'} />
                      ))}
                    </Pie>
                    <RechartsTooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </Box>
            </CardBody>
          </Card>
        </SimpleGrid>
      </AnimatedBox>

      {/* Filters */}
      <AnimatedBox animation="fadeIn" delay={0.4} duration={0.5}>
        <Flex gap={4} mb={6} flexWrap="wrap">
          <InputGroup maxW="300px">
            <InputLeftElement pointerEvents="none">
              <Icon as={FaSearch} color="gray.400" />
            </InputLeftElement>
            <Input
              placeholder="Search businesses..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              bg={cardBg}
              borderColor={borderColor}
            />
          </InputGroup>
          <Select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            maxW="200px"
            bg={cardBg}
            borderColor={borderColor}
          >
            <option value="ranking">Sort by Score</option>
            <option value="name">Sort by Name</option>
            <option value="growth">Sort by Growth Rank</option>
            <option value="reviews">Sort by Reviews Rank</option>
          </Select>
          <Button
            variant="outline"
            onClick={() => {
              const allExpanded = filteredAndSortedData.every(b => expandedCards[b.business_name]);
              const newState = {};
              filteredAndSortedData.forEach(b => {
                newState[b.business_name] = !allExpanded;
              });
              setExpandedCards(newState);
            }}
          >
            {Object.values(expandedCards).some(v => v) ? 'Collapse All' : 'Expand All'}
          </Button>
        </Flex>
      </AnimatedBox>

      {/* Business Cards */}
      <VStack spacing={0} align="stretch">
        {filteredAndSortedData.map((business, index) => (
          <BusinessRankingCard
            key={business.business_name}
            business={business}
            position={index + 1}
            totalBusinesses={totalBusinesses}
            isExpanded={expandedCards[business.business_name]}
            onToggle={() => toggleCard(business.business_name)}
            onBusinessClick={handleBusinessClick}
          />
        ))}
      </VStack>

      {filteredAndSortedData.length === 0 && (
        <Flex justify="center" align="center" py={12}>
          <VStack>
            <Icon as={FaSearch} color="gray.400" boxSize={12} />
            <Text color={subtitleColor}>No businesses found matching your search</Text>
          </VStack>
        </Flex>
      )}

      {/* Methodology Explainer */}
      <AnimatedBox animation="slideUp" delay={0.1} duration={0.6}>
        <Card
          bgGradient={bgGradient}
          borderWidth="1px"
          borderColor={borderColor}
          borderRadius="xl"
          mt={8}
          overflow="hidden"
        >
          <CardBody p={6}>
            <Grid templateColumns={{ base: '1fr', lg: '2fr 1fr' }} gap={6}>
              <VStack align="start" spacing={4}>
                <HStack>
                  <Icon as={FaCalculator} color="blue.500" boxSize={6} />
                  <Heading size="md" color={textColor}>How We Calculate {brandName} Scores</Heading>
                </HStack>
                <Text color={subtitleColor} fontSize="sm">
                  The <strong>{brandName} Score</strong> measures overall business performance on a <strong>0-100 scale</strong> where <strong>100 is the best</strong>. Each of the 5 categories contributes up to <strong>20 points</strong> based on ranking position: 1st = 20 points, 2nd = 19, ..., 20th = 1, 21st+ = 0. A business ranked #1 in all categories scores 100 (20+20+20+20+20).
                </Text>
                
                <Box w="full" p={4} bg={cardBg} borderRadius="lg" borderWidth="1px" borderColor={borderColor}>
                  <Text fontWeight="bold" mb={2} color={textColor}>How Scoring Works:</Text>
                  <Text fontSize="sm" color="blue.600" mb={3}>
                    Each of the 5 categories awards 0-20 points based on your ranking position. 1st = 20 pts, 2nd = 19 pts, ..., 20th = 1 pt, 21st+ = 0 pts. The sum gives you a total score out of 100.
                  </Text>
                  <Text fontFamily="mono" fontSize="xs" color={subtitleColor} mb={2}>
                    Points per category = max(0, 21 - rank)
                  </Text>
                  <Text fontFamily="mono" fontSize="xs" color={subtitleColor}>
                    Total Score = Growth Points + Reviews Points + Traffic Points + Sentiment Points + SEO Points
                  </Text>
                  <Box mt={3} p={2} bg="blue.50" borderRadius="md">
                    <Text fontSize="xs" fontWeight="bold" color="blue.700">
                      Examples:
                    </Text>
                    <Text fontSize="xs" color={subtitleColor} mt={1}>
                      • Ranked #1 in all 5 categories → 20+20+20+20+20 = <strong>100 points</strong>
                    </Text>
                    <Text fontSize="xs" color={subtitleColor}>
                      • Ranked #5 in all 5 categories → 16+16+16+16+16 = <strong>80 points</strong>
                    </Text>
                  </Box>
                </Box>
                
                <Box w="full" p={4} bg="purple.50" borderRadius="lg" borderWidth="1px" borderColor={borderColor}>
                  <Text fontWeight="bold" mb={2} color={textColor}>Performance Tiers:</Text>
                  <SimpleGrid columns={{ base: 1, sm: 2 }} spacing={2}>
                    <Text fontSize="xs" color={subtitleColor}>• <strong>100:</strong> Perfect</Text>
                    <Text fontSize="xs" color={subtitleColor}>• <strong>80-99:</strong> Exceptional</Text>
                    <Text fontSize="xs" color={subtitleColor}>• <strong>60-79:</strong> Excellent</Text>
                    <Text fontSize="xs" color={subtitleColor}>• <strong>40-59:</strong> Strong</Text>
                    <Text fontSize="xs" color={subtitleColor}>• <strong>20-39:</strong> Developing</Text>
                    <Text fontSize="xs" color={subtitleColor}>• <strong>0-19:</strong> Emerging</Text>
                  </SimpleGrid>
                </Box>
                
                <SimpleGrid columns={{ base: 2, md: 3, lg: 5 }} spacing={4} w="full">
                  {[
                    { icon: HiOutlineTrendingUp, label: 'Growth', description: 'Review growth momentum over 3 months' },
                    { icon: FaComments, label: 'Total Reviews', description: 'Overall review volume and presence' },
                    { icon: FaGlobe, label: 'Web Traffic', description: 'Monthly website visitor estimates' },
                    { icon: FaStar, label: 'Sentiment', description: 'Positive review percentage' },
                    { icon: FaSearch, label: 'SEO Score', description: 'Search engine optimization performance' },
                  ].map((item) => (
                    <Card key={item.label} bg={cardBg} borderWidth="1px" borderColor={borderColor} borderRadius="lg">
                      <CardBody p={4} textAlign="center">
                        <Icon as={item.icon} color="blue.500" boxSize={6} mb={2} />
                        <Text fontSize="sm" fontWeight="medium" color={textColor}>{item.label}</Text>
                        <Text fontSize="xs" color={subtitleColor} mt={1}>{item.description}</Text>
                      </CardBody>
                    </Card>
                  ))}
                </SimpleGrid>
              </VStack>

              {/* Score Tiers Legend */}
              <VStack align="stretch" spacing={3}>
                <HStack>
                  <Icon as={FaBalanceScale} color="purple.500" />
                  <Text fontWeight="semibold" color={textColor}>Score Tiers (0-100)</Text>
                </HStack>
                {SCORE_TIERS.map(tier => (
                  <HStack key={tier.label} spacing={3}>
                    <Badge colorScheme={tier.color} px={2} py={1} borderRadius="md" minW="60px" textAlign="center">
                      {tier.min === tier.max ? tier.min : `${tier.min}-${tier.max}`}
                    </Badge>
                    <VStack align="start" spacing={0}>
                      <Text fontSize="sm" fontWeight="medium" color={textColor}>{tier.label}</Text>
                      <Text fontSize="xs" color={subtitleColor}>{tier.description}</Text>
                    </VStack>
                  </HStack>
                ))}
              </VStack>
            </Grid>
          </CardBody>
        </Card>
      </AnimatedBox>

      {/* Business Review Modal */}
      <BusinessReviewModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setSelectedBusiness(null);
        }}
        business={selectedBusiness}
        yearData={yearData}
        sortedByTotal={sortedByTotal}
        sortedBySentiment={sortedBySentiment}
        sortedByGrowth={sortedByGrowth}
        sortedByTraffic={sortedByTraffic}
        sortedBySeo={rankingData?.sortedBySeo || []}
        demoMode={demoMode}
        demoTrafficHistory={demoMode ? demoData?.modalTrafficHistory : undefined}
        demoPricingData={demoMode ? demoData?.buildDemoPricing : undefined}
      />
    </Box>
  );
};

export default LocalyticsScoreDashboard;
